import {put, call} from 'redux-saga/effects';
import Firm from "../../entities/firm";
import * as Actions from "../actions/firmActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type FirmAction = {
    firmId: string;
    firm: Firm;
    firms: Firm[];
};

export function* fetchOneFirm(action: FirmAction) {
	try {
		const firm: Firm = yield call(api.findOne, 'firm', action.firmId);

		// Now we can update the user interface
		yield put({type: Actions.FIRM_FETCH_ONE_SUCCESS, firmId: action.firmId, firm});
	}
	catch(err) {
		yield put({type: Actions.FIRM_FETCH_ONE_FAILURE, firmId: action.firmId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {FirmAction} action
 */
export function* saveFirm(action: FirmAction) {
    // console.debug('⮂ FIRM_SAVE saga', action.firm?.id);
    
	if (action.firm === undefined || action.firm === null) {
		console.error('saveFirm received a null');
		throw new Error('saveFirm received a null');
	}

	if (!(action.firm instanceof Firm)) {
		action.firm = new Firm(action.firm);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'firm', action.firm);
		if (addResult.isErr()) {
			console.error(`⮂ saveFirm: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FIRM_SAVE_FAILURE, firm: action.firm, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FIRM_SAVE, firm: action.firm});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'firm', action.firm.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.FIRM_SAVE_FAILURE, firm: action.firm, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {FirmAction} action
 * @param {Firm[]} action.firms
 */
export function* saveManyFirms(action: FirmAction) {
    // console.debug('⮂ FIRM_SAVE_MANY saga', action.firms.map(firm => firm.id).join(','));
    
    if (!Array.isArray(action.firms)) {
        console.error('saveManyFirms expected an array, received', action.firms);
        throw new Error('saveManyFirms expected an array');
    }

	try {
		const toSave: Firm[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.firms.length; i++) {
			const firm = action.firms[i];
			firm.persisted = false;
			toSave.push(firm);
		}

		const addResult = yield call(idb.add, 'firm', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyFirms: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FIRM_SAVE_MANY_FAILURE, firms: action.firms, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FIRM_SAVE_MANY, firms: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.FIRM_SAVE_MANY_FAILURE, firms: action.firms, status: 500, err});

		console.error(`Error saving ${action.firms.length} firms to database`, err);
	}
}

/**
 * @param {FirmAction} action
 */
export function* removeFirm(action: FirmAction) {
    const userId = getCurrentUserId();
	if (!userId || action.firm === undefined || action.firm === null || !(action.firm instanceof Firm)) {
			console.error('removeFirm expected type Firm, received', action.firm);
			throw new Error('removeFirm expected type Firm');
		}

	try {
		// Mark as deleted
		action.firm.status = 'D';
		action.firm.addedOn = newHRTimestamp();
		action.firm.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'firm', action.firm);
		if (addResult.isErr()) {
			console.error(`⮂ removeFirm: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FIRM_REMOVE_FAILURE, firm: action.firm, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FIRM_REMOVE, firm: action.firm});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.FIRM_REMOVE_FAILURE, firm: action.firm, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this Firm.
	*
	* @param {FirmAction} action
	*/
export function* restoreFirm(action: FirmAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreFirm expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.firm === undefined || action.firm === null) {
        console.error('restoreFirm expected a Firm, received null');
        throw new Error('restoreFirm expected a Firm, received null');
    }
    else if (!(action.firm instanceof Firm)) action.firm = new Firm(action.firm); 
    
	try {
		// Mark as restored
		action.firm.status = 'U';
		action.firm.addedOn = newHRTimestamp();
		action.firm.addedBy = userId;
		action.firm.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'firm', action.firm);
		if (addResult.isErr()) {
			console.error(`⮂ restoreFirm: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.FIRM_RESTORE_FAILURE, firm: action.firm, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.FIRM_RESTORE, firm: action.firm});
	}
	catch(err) {
		yield put({type: Actions.FIRM_RESTORE_FAILURE, firm: action.firm, status: 500, err});

		console.error('Error restoring from database', err);
	}
}